'use client';

import { useEffect, useId, useState } from 'react';

import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import type { ArticleHeading } from './article-html';

type TocProps = {
  items: ArticleHeading[];
  /** „Obsah článku“. */
  title: string;
  /** Id of the article element – the reading progress is measured on it. */
  articleId: string;
  className?: string;
};

/**
 * The section being read and how much of the article has been read. A heading is current once it
 * passes the reading line (a quarter of the window, at least below the sticky header and the anchor
 * margin); the progress is the part of the article above the bottom of the window.
 */
function useReadingState(items: ArticleHeading[], articleId: string) {
  const [state, setState] = useState({ active: items[0]?.id ?? '', progress: 0 });
  const ids = items.map(item => item.id).join('\n');

  useEffect(() => {
    const article = document.getElementById(articleId);
    const headings = ids
      .split('\n')
      .map(id => document.getElementById(id))
      .filter((heading): heading is HTMLElement => !!heading);
    if (!article) return;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const rect = article.getBoundingClientRect();
        const read = (window.innerHeight - rect.top) / Math.max(rect.height, 1);
        const progress = Math.round(Math.min(Math.max(read, 0), 1) * 100);
        const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop);
        const line = Math.max((padding || 0) + 32, window.innerHeight / 4);
        let active = headings[0]?.id ?? '';
        for (const heading of headings) {
          if (heading.getBoundingClientRect().top <= line) active = heading.id;
        }
        setState(current =>
          current.active === active && current.progress === progress
            ? current
            : { active, progress },
        );
      });
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [ids, articleId]);

  return state;
}

/** Figma „Item“ (250:15344): Body/M #363D36, 10 × 12 px, radius 8; current = 250:15346. */
function TocLinks({
  items,
  active,
  onNavigate,
  id,
}: {
  items: ArticleHeading[];
  active: string;
  onNavigate?: () => void;
  id?: string;
}) {
  return (
    <ol id={id} role="list" className="flex flex-col gap-1">
      {items.map(item => {
        const current = item.id === active;
        return (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              aria-current={current ? 'location' : undefined}
              onClick={onNavigate}
              className={cn(
                'flex items-center gap-2 rounded-[8px] px-3 py-2.5 transition-colors',
                'focus-visible:shadow-focus focus-visible:outline-none',
                current
                  ? 'nt-body-m-bold bg-[#F6FBF7] text-[#0E6A20]'
                  : 'nt-body-m text-[#363D36] hover:bg-[#F5F6F3] hover:text-[#0D110D]',
              )}
            >
              {current ? (
                <span
                  aria-hidden="true"
                  className="h-[18px] w-[3px] shrink-0 rounded-[2px] bg-[#127A26]"
                />
              ) : null}
              {item.label}
            </a>
          </li>
        );
      })}
    </ol>
  );
}

/** Track of the reading progress (Figma „Track“ 250:15356 – 6 px; mobile 4 px). */
function ProgressTrack({ progress, className }: { progress: number; className?: string }) {
  return (
    <span aria-hidden="true" className={cn('block overflow-hidden bg-[#E2E5DF]', className)}>
      <span
        className="block h-full rounded-[inherit] bg-[#127A26] transition-[width] duration-150 motion-reduce:transition-none"
        style={{ width: `${progress}%` }}
      />
    </span>
  );
}

/**
 * Figma „Obsah článku“ in the sidebar (250:15341): the H5 title, the `h2`s of the article with the
 * current one marked, and „{pocet} % prečítané“ under them.
 */
export function TocCard({
  items,
  title,
  articleId,
  progressText,
  className,
}: TocProps & {
  /** „{pocet} % prečítané“ – the bar alone when the field is empty. */
  progressText: string;
}) {
  const titleId = useId();
  const { active, progress } = useReadingState(items, articleId);

  return (
    <nav
      aria-labelledby={titleId}
      className={cn(
        'flex flex-col gap-1 rounded-2xl border border-[#E2E5DF] px-4 pb-4 pt-6',
        className,
      )}
    >
      <h2 id={titleId} className="nt-h5 px-2 pb-2 text-[#0D110D]">
        {title}
      </h2>
      <TocLinks items={items} active={active} />
      <div className="flex items-center gap-2.5 px-3 pt-2">
        <ProgressTrack progress={progress} className="h-1.5 flex-1 rounded-[3px]" />
        {progressText ? (
          <span className="nt-caption shrink-0 text-[#5D655D]">
            {fillTemplate(progressText, { pocet: progress })}
          </span>
        ) : null}
      </div>
    </nav>
  );
}

/**
 * Figma mobile „Obsah článku“ (310:17776): a collapsed block above the article – list icon, the
 * title (Heading/H7 = `nt-title-s`) with the current section under it, the caret and the 4 px
 * progress bar. Opening it shows the sections; choosing one closes it.
 */
export function TocDisclosure({ items, title, articleId, className }: TocProps) {
  const titleId = useId();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const { active, progress } = useReadingState(items, articleId);
  const current = items.find(item => item.id === active)?.label ?? '';

  return (
    <nav
      aria-labelledby={titleId}
      className={cn('flex flex-col gap-3 rounded-2xl border border-[#E2E5DF] p-4', className)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(value => !value)}
        className="flex items-center gap-2.5 rounded-xl text-left focus-visible:shadow-focus focus-visible:outline-none"
      >
        <Icon name="list-bullets" className="text-[#127A26]" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span id={titleId} className="nt-title-s text-[#0D110D]">
            {title}
          </span>
          {current ? (
            <span className="nt-caption truncate text-[#0E6A20] max-md:text-[10px]">{current}</span>
          ) : null}
        </span>
        <Icon
          name="caret-down"
          size={18}
          className={cn(
            'text-[#0D110D] transition-transform motion-reduce:transition-none',
            open && 'rotate-180',
          )}
        />
      </button>
      <div hidden={!open}>
        <TocLinks id={listId} items={items} active={active} onNavigate={() => setOpen(false)} />
      </div>
      <ProgressTrack progress={progress} className="h-1 rounded-[2px]" />
    </nav>
  );
}
